import React from 'react';
import Card from '../../Card';
import DegradedBanner from '../DegradedBanner';

// ISSUE-014：潜力维度（is_potential=true，60-70 分）用中性蓝灰；
// 真优势按 score 配色保持原状
const POTENTIAL_COLOR = '#64748b';

function scoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#2563eb';
  return '#d97706';
}

/**
 * V4 P3：优势维度。
 *
 * ISSUE-014（思路 C）：cards 含两类：
 *   - is_potential=false：真优势（score >= 70），文案语气"突出/实战水平"
 *   - is_potential=true：潜力维度（60-70 分补位），UI 用中性灰边 + "潜力"标签，
 *     避免把 65 分硬塞成"优势"恭维用户
 *
 * narrative_v4.strength_cards = [
 *   { text, model_used, degraded, attempts,
 *     dimension_id, dimension_name, score, is_potential },
 *   ...
 * ]
 */
export default function V4P3Strengths({ report }) {
  const cards = report?.narrative_v4?.strength_cards || [];

  if (cards.length === 0) {
    return (
      <div data-testid="report-v4-p3">
        <Card style={{ padding: '24px 28px' }}>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            本次未能识别出明显的优势维度，请重点查看下一页的欠缺分析。
          </p>
        </Card>
      </div>
    );
  }

  const hasPotential = cards.some(c => c.is_potential);
  const allPotential = cards.every(c => c.is_potential);
  const headerCopy = allPotential
    ? `以下 ${cards.length} 个维度是本次测评中你的潜力维度（基础已具备，仍有提升空间）。`
    : hasPotential
      ? `以下 ${cards.length} 个维度是本次测评中你表现较好的部分（含潜力维度）。`
      : `以下 ${cards.length} 个维度是本次测评中你表现最突出的部分。`;

  return (
    <div data-testid="report-v4-p3">
      <Card style={{ padding: '20px 28px', marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: 18 }}>
          {allPotential ? '潜力维度' : '优势维度'}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
          {headerCopy}
        </p>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {cards.map((c, i) => {
          const score = c.score;
          const isPotential = !!c.is_potential;
          const sColor = isPotential
            ? POTENTIAL_COLOR
            : (typeof score === 'number' ? scoreColor(score) : 'var(--color-primary)');
          return (
            <Card
              key={c.dimension_id ?? `s-${i}`}
              data-testid={`report-v4-p3-card-${i}`}
              data-is-potential={isPotential ? 'true' : 'false'}
              style={{ padding: '20px 26px', borderLeft: `4px solid ${sColor}` }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                marginBottom: 12, gap: 12, flexWrap: 'wrap',
              }}>
                <h3 style={{
                  fontSize: 16, fontWeight: 700, margin: 0,
                  minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere',
                }}>
                  {c.dimension_name || `优势 ${i + 1}`}
                  {isPotential && (
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 600,
                      color: POTENTIAL_COLOR, background: '#f1f5f9',
                      padding: '2px 8px', borderRadius: 10,
                      whiteSpace: 'nowrap', verticalAlign: 'middle',
                    }}>
                      潜力
                    </span>
                  )}
                </h3>
                {typeof score === 'number' && (
                  <span style={{ fontSize: 15, fontWeight: 700, color: sColor, whiteSpace: 'nowrap' }}>
                    {score} 分
                  </span>
                )}
              </div>
              {c.degraded && <DegradedBanner />}
              <p style={{
                fontSize: 14, lineHeight: 1.8, color: 'var(--color-text)',
                whiteSpace: 'pre-wrap', margin: 0,
              }}>
                {c.text || '（暂无内容）'}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
